import React, { useCallback, useEffect, useState } from "react";

import { useAuth } from "../../contextos/AuthContext";
import { estilosGrupos } from "../../elementos/gruposEstilos";
import {
  aprobarSolicitud,
  archivarGrupo,
  cargarMiembros,
  cederAdministracion,
  rechazarSolicitud,
  retirarMiembro,
} from "../../funciones/gruposServicio";

const aFecha = (valor) =>
  valor && typeof valor.toDate === "function" ? valor.toDate() : null;

const fechaCorta = (valor) => {
  const fecha = aFecha(valor);
  return fecha
    ? fecha.toLocaleDateString("es-CO", { day: "numeric", month: "short" })
    : "";
};

export default function PanelAdministrarGrupo({ grupo, abierto, onCerrar, onCambio }) {
  const { usuario } = useAuth();

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState("");
  const [error, setError] = useState("");

  const cargar = useCallback(async () => {
    if (!grupo) return;
    setCargando(true);
    setError("");
    try {
      setDatos(await cargarMiembros(grupo));
    } catch (err) {
      console.error("Error cargando miembros:", err);
      setError("No fue posible cargar los miembros del grupo.");
    } finally {
      setCargando(false);
    }
  }, [grupo]);

  useEffect(() => {
    if (abierto) cargar();
  }, [abierto, cargar]);

  useEffect(() => {
    if (!abierto) return undefined;
    const alPresionar = (e) => e.key === "Escape" && onCerrar();
    window.addEventListener("keydown", alPresionar);
    return () => window.removeEventListener("keydown", alPresionar);
  }, [abierto, onCerrar]);

  if (!abierto || !grupo) return null;

  // Ejecuta una acción, refresca y avisa al padre
  const ejecutar = async (clave, accion, { cerrarAlTerminar = false } = {}) => {
    setProcesando(clave);
    setError("");
    try {
      await accion();
      if (cerrarAlTerminar) {
        await onCambio?.();
        onCerrar();
      } else if (onCambio) {
        // El padre recarga los grupos; al llegar el grupo actualizado,
        // el efecto de este panel vuelve a cargar los miembros.
        await onCambio();
      } else {
        await cargar();
      }
    } catch (err) {
      console.error("Error en acción de administración:", err);
      setError(err.message?.startsWith("El ") || err.message?.startsWith("Solo ")
        ? err.message
        : "No fue posible completar la acción. Revisa la conexión e inténtalo de nuevo.");
    } finally {
      setProcesando("");
    }
  };

  const quitar = (miembro) => {
    const tipo = miembro.rol === "profesor" ? "profesor" : "estudiante";
    if (!window.confirm(`¿Quitar a ${miembro.nombre} del grupo como ${tipo}? Su historial se conserva.`)) return;
    ejecutar(`quitar-${miembro.usuarioId}`, () => retirarMiembro(grupo, miembro));
  };

  const ceder = (profesor) => {
    if (!window.confirm(
      `${profesor.nombre} pasará a ser el administrador del grupo. Tú seguirás como profesor, pero ya no podrás aprobar, quitar miembros ni archivar el grupo. ¿Continuar?`
    )) return;
    ejecutar(`ceder-${profesor.usuarioId}`, () => cederAdministracion(grupo, profesor.usuarioId), {
      cerrarAlTerminar: true,
    });
  };

  const archivar = () => {
    if (!window.confirm(
      `¿Archivar "${grupo.nombre}"? Dejará de verse para profesores y estudiantes y sus enlaces de invitación se desactivarán. La información se conserva.`
    )) return;
    ejecutar("archivar", () => archivarGrupo(grupo), { cerrarAlTerminar: true });
  };

  return (
    <div className="gg gg-fondo" onMouseDown={(e) => e.target === e.currentTarget && onCerrar()}>
      <style>{estilosGrupos}</style>

      <div className="gg-modal gg-modal--ancho" role="dialog" aria-modal="true" aria-labelledby="gg-titulo-admin">
        <div className="gg-modal__cabecera">
          <div>
            <h2 id="gg-titulo-admin">Administrar grupo</h2>
            <p className="gg-sutil">{grupo.nombre}</p>
          </div>
          <button type="button" className="gg-cerrar" onClick={onCerrar} aria-label="Cerrar">×</button>
        </div>

        {error && <p role="alert" className="gg-alerta">{error}</p>}
        {cargando && !datos && <p className="gg-sutil">Cargando miembros…</p>}

        {datos && (
          <>
            {/* SOLICITUDES */}
            <section className="gg-seccion">
              <h3 className="gg-seccion__titulo">
                Solicitudes pendientes
                {datos.pendientes.length > 0 && (
                  <span className="gg-insignia gg-insignia--aviso">{datos.pendientes.length}</span>
                )}
              </h3>

              {datos.pendientes.length === 0 ? (
                <p className="gg-sutil">No hay solicitudes por revisar.</p>
              ) : (
                <ul className="gg-filas">
                  {datos.pendientes.map((m) => (
                    <li key={m.docId} className="gg-fila">
                      <div>
                        <div className="gg-fila__nombre">{m.nombre}</div>
                        <span className="gg-sutil">
                          Quiere unirse como {m.rol === "profesor" ? "profesor" : "estudiante"}
                          {fechaCorta(m.fechaSolicitud) && ` · ${fechaCorta(m.fechaSolicitud)}`}
                        </span>
                      </div>
                      <div className="gg-fila__acciones">
                        <button
                          type="button"
                          className="gg-btn gg-btn--chico gg-btn--exito"
                          disabled={!!procesando}
                          onClick={() => ejecutar(`aprobar-${m.docId}`, () => aprobarSolicitud(grupo, m, usuario.uid))}
                        >
                          Aprobar
                        </button>
                        <button
                          type="button"
                          className="gg-btn gg-btn--chico"
                          disabled={!!procesando}
                          onClick={() => ejecutar(`rechazar-${m.docId}`, () => rechazarSolicitud(m))}
                        >
                          Rechazar
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* PROFESORES */}
            <section className="gg-seccion">
              <h3 className="gg-seccion__titulo">
                Profesores <span className="gg-insignia">{datos.profesores.length}</span>
              </h3>
              <ul className="gg-filas">
                {datos.profesores.map((p) => (
                  <li key={p.usuarioId} className="gg-fila">
                    <div className="gg-fila__nombre">
                      {p.nombre}{" "}
                      {p.esAdmin && <span className="gg-insignia gg-insignia--admin">Administrador</span>}
                      {p.usuarioId === usuario.uid && <span className="gg-sutil"> (tú)</span>}
                    </div>
                    {!p.esAdmin && (
                      <div className="gg-fila__acciones">
                        <button
                          type="button"
                          className="gg-btn gg-btn--chico"
                          disabled={!!procesando}
                          onClick={() => ceder(p)}
                        >
                          Hacer administrador
                        </button>
                        <button
                          type="button"
                          className="gg-btn gg-btn--chico gg-btn--peligro"
                          disabled={!!procesando}
                          onClick={() => quitar(p)}
                        >
                          Quitar
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>

            {/* ESTUDIANTES */}
            <section className="gg-seccion">
              <h3 className="gg-seccion__titulo">
                Estudiantes <span className="gg-insignia">{datos.estudiantes.length}</span>
              </h3>
              {datos.estudiantes.length === 0 ? (
                <p className="gg-sutil">Aún no hay estudiantes activos. Compárteles el enlace de invitación.</p>
              ) : (
                <ul className="gg-filas">
                  {datos.estudiantes.map((m) => (
                    <li key={m.docId} className="gg-fila">
                      <div>
                        <div className="gg-fila__nombre">{m.nombre}</div>
                        {fechaCorta(m.fechaIngreso) && (
                          <span className="gg-sutil">Desde {fechaCorta(m.fechaIngreso)}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="gg-btn gg-btn--chico gg-btn--peligro"
                        disabled={!!procesando}
                        onClick={() => quitar(m)}
                      >
                        Quitar
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* ARCHIVAR */}
            <section className="gg-zona-cuidado">
              <h3 className="gg-seccion__titulo">Archivar grupo</h3>
              <p className="gg-sutil" style={{ marginBottom: 10 }}>
                El grupo se oculta para todos y sus enlaces dejan de funcionar. Nada se borra.
              </p>
              <button
                type="button"
                className="gg-btn gg-btn--peligro"
                disabled={!!procesando}
                onClick={archivar}
              >
                Archivar este grupo
              </button>
            </section>
          </>
        )}
      </div>
    </div>
  );
}